import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import apiClient from '../services/api';

const PermissionContext = createContext(null);

export const PermissionProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [globalRole, setGlobalRole] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadPermissions = useCallback(async () => {
    if (!isAuthenticated) {
      setPermissions([]);
      setGlobalRole(null);
      setIsSuperAdmin(false);
      setLoading(false);
      return;
    }

    try {
      const response = await apiClient.get('/auth/me/permissions');
      const data = response.data;
      setPermissions(data.permissions || []);
      setGlobalRole(data.global_role);
      setIsSuperAdmin(data.is_super_admin || false);
    } catch (error) {
      console.warn('Could not load permissions:', error.message);
      setPermissions([]);
      setIsSuperAdmin(false);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions, user]);

  const hasPermission = useCallback((permission) => {
    if (isSuperAdmin) return true;
    return permissions.includes(permission);
  }, [permissions, isSuperAdmin]);

  const hasAnyPermission = useCallback((perms) => {
    if (isSuperAdmin) return true;
    return perms.some(p => permissions.includes(p));
  }, [permissions, isSuperAdmin]);

  const hasAllPermissions = useCallback((perms) => {
    if (isSuperAdmin) return true;
    return perms.every(p => permissions.includes(p));
  }, [permissions, isSuperAdmin]);

  const canAccessAdmin = useCallback(() => {
    if (isSuperAdmin) return true;
    const adminPerms = [
      'system.config.read', 'system.config.update',
      'user.create.tenant', 'user.update.tenant',
      'role.assign.tenant', 'role.assign.global'
    ];
    return adminPerms.some(p => permissions.includes(p));
  }, [permissions, isSuperAdmin]);

  const value = {
    permissions,
    globalRole,
    isSuperAdmin,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessAdmin,
    reloadPermissions: loadPermissions
  };

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  return context;
};
